import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Snackbar,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import MenuIcon from "@mui/icons-material/Menu";
import WorkOutlineIcon from "@mui/icons-material/WorkOutline";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import AnalyticsIcon from "@mui/icons-material/Analytics";
import ConfirmationNumberOutlinedIcon from "@mui/icons-material/ConfirmationNumberOutlined";
import QueryStatsOutlinedIcon from "@mui/icons-material/QueryStatsOutlined";
import WifiIcon from "@mui/icons-material/Wifi";
import CategoryIcon from "@mui/icons-material/Category";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import SmartToyOutlined from "@mui/icons-material/SmartToyOutlined";
import BarChartIcon from "@mui/icons-material/BarChart";
import ForumOutlinedIcon from "@mui/icons-material/ForumOutlined";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import AssignmentLateOutlinedIcon from "@mui/icons-material/AssignmentLateOutlined";
import BugReportOutlinedIcon from "@mui/icons-material/BugReportOutlined";
import QrCode2OutlinedIcon from "@mui/icons-material/QrCode2Outlined";
import StorageOutlinedIcon from "@mui/icons-material/StorageOutlined";
import AccountBalanceOutlinedIcon from "@mui/icons-material/AccountBalanceOutlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import { Link as RouterLink, Outlet, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "react-query";
import { BusyButton } from "./shared";
import {
  FEATURE_PLATFORM_CONSOLIDATED_DASHBOARD_ENABLED,
  FEATURE_PLATFORM_INTERNAL_CHAT_ENABLED,
} from "../../../constants/featureFlags";
import { PLATFORM_CONSOLE_BASE, PLATFORM_CONSOLE_PATH, PLATFORM_CONSOLE_SEGMENT, isConsoleTeamPath } from "./platformConsoleRoutes";
import { usePlatformConsoleAcl } from "./PlatformConsoleAclContext";
import { clearActiveWorkspaceBrowserStorage } from "../../../utils/workspaceLocalStorage";
import { useBrand } from "../../../context/BrandContext";
import { PLATFORM_APP_VERSION } from "../../../appVersion";


const DRAWER_WIDTH = 272;

function navSectionKeyFromPath(navPath) {
  const s = String(navPath || "").replace(`${PLATFORM_CONSOLE_BASE}/`, "").split("/")[0];
  return s || "workspaces";
}

const NAV_ITEMS = [
  { path: PLATFORM_CONSOLE_PATH.workspaces, match: `${PLATFORM_CONSOLE_BASE}/workspaces`, Icon: WorkOutlineIcon, labelKey: "platformPage.tabWorkspaces" },
  {
    path: PLATFORM_CONSOLE_PATH.whatsappOut2026,
    match: PLATFORM_CONSOLE_PATH.whatsappOut2026,
    Icon: WhatsAppIcon,
    labelKey: "platformPage.navWhatsappOut2026",
    section: PLATFORM_CONSOLE_SEGMENT.workspaces,
  },
  {
    path: PLATFORM_CONSOLE_PATH.opportunities,
    match: `${PLATFORM_CONSOLE_BASE}/opportunities`,
    Icon: TrendingUpIcon,
    labelKey: "platformPage.tabOpportunities",
  },
  { path: PLATFORM_CONSOLE_PATH.insights, match: `${PLATFORM_CONSOLE_BASE}/insights`, Icon: AnalyticsIcon, labelKey: "platformPage.navInsights" },
  {
    path: `${PLATFORM_CONSOLE_PATH.dashboard}/live`,
    match: `${PLATFORM_CONSOLE_BASE}/dashboard`,
    Icon: BarChartIcon,
    labelKey: "platformPage.navDashboard",
    section: PLATFORM_CONSOLE_SEGMENT.dashboard,
    featureEnabled: FEATURE_PLATFORM_CONSOLIDATED_DASHBOARD_ENABLED,
  },

  {
    path: PLATFORM_CONSOLE_PATH.productUsage,
    match: `${PLATFORM_CONSOLE_BASE}/product-usage`,
    Icon: QueryStatsOutlinedIcon,
    labelKey: "platformPage.tabProductUsage",
  },
  {
    path: PLATFORM_CONSOLE_PATH.passengerTicketSales,
    match: `${PLATFORM_CONSOLE_BASE}/passenger-ticket-sales`,
    Icon: ConfirmationNumberOutlinedIcon,
    labelKey: "platformPage.tabPassengerTicketSales",
    section: PLATFORM_CONSOLE_SEGMENT.passengerTicketSales,
  },
  { path: PLATFORM_CONSOLE_PATH.presence, match: `${PLATFORM_CONSOLE_BASE}/presence`, Icon: WifiIcon, labelKey: "platformPage.navPresence" },
  { path: PLATFORM_CONSOLE_PATH.segments, match: `${PLATFORM_CONSOLE_BASE}/segments`, Icon: CategoryIcon, labelKey: "platformPage.tabSegments" },
  {
    path: PLATFORM_CONSOLE_PATH.governance,
    match: `${PLATFORM_CONSOLE_BASE}/governance`,
    Icon: AdminPanelSettingsIcon,
    labelKey: "platformPage.tabGovernance",
  },
  { path: PLATFORM_CONSOLE_PATH.catalog, match: `${PLATFORM_CONSOLE_BASE}/catalog`, Icon: Inventory2Icon, labelKey: "platformPage.tabCatalogOps" },
  {
    path: PLATFORM_CONSOLE_PATH.chatbotTemplates,
    match: `${PLATFORM_CONSOLE_BASE}/chatbot-templates`,
    Icon: SmartToyOutlined,
    labelKey: "platformPage.tabChatbotTemplates",
  },
  {
    path: PLATFORM_CONSOLE_PATH.pendingActions,
    match: `${PLATFORM_CONSOLE_BASE}/pending-actions`,
    Icon: AssignmentLateOutlinedIcon,
    labelKey: "platformPage.tabPendingActions",
    section: PLATFORM_CONSOLE_SEGMENT.pendingActions,
  },
  {
    path: PLATFORM_CONSOLE_PATH.platformErrors,
    match: `${PLATFORM_CONSOLE_BASE}/platform-errors`,
    Icon: BugReportOutlinedIcon,
    labelKey: "platformPage.tabPlatformErrors",
    section: PLATFORM_CONSOLE_SEGMENT.platformErrors,
  },
  {
    path: PLATFORM_CONSOLE_PATH.dbPressure,
    match: `${PLATFORM_CONSOLE_BASE}/db-pressure`,
    Icon: StorageOutlinedIcon,
    labelKey: "platformPage.tabDbOps",
    // Mesma audiência ops dos erros da plataforma (sem nova chave ACL).
    section: PLATFORM_CONSOLE_SEGMENT.platformErrors,
  },
  {
    path: PLATFORM_CONSOLE_PATH.apifacilInstances,
    match: `${PLATFORM_CONSOLE_BASE}/apifacil-instances`,
    Icon: QrCode2OutlinedIcon,
    labelKey: "platformPage.tabApifacilInstances",
    section: PLATFORM_CONSOLE_SEGMENT.apifacilInstances,
  },
  {
    path: PLATFORM_CONSOLE_PATH.financeStatements,
    match: `${PLATFORM_CONSOLE_BASE}/finance`,
    Icon: AccountBalanceOutlinedIcon,
    labelKey: "platformPage.navFinance",
    section: PLATFORM_CONSOLE_SEGMENT.finance,
  },
  {
    path: PLATFORM_CONSOLE_PATH.myIncentives,
    match: `${PLATFORM_CONSOLE_BASE}/my-incentives`,
    Icon: PaymentsOutlinedIcon,
    labelKey: "platformPage.navMyIncentives",
    requiresFinancePerson: true,
  },
  {
    path: PLATFORM_CONSOLE_PATH.team,
    match: `${PLATFORM_CONSOLE_BASE}/team`,
    Icon: ForumOutlinedIcon,
    labelKey: "platformPage.navTeam",
    section: "presence",
    featureEnabled: FEATURE_PLATFORM_INTERNAL_CHAT_ENABLED,
  },
];


function pathMatchesNav(pathname, navMatch) {
  if (pathname === navMatch) return true;
  if (pathname.startsWith(`${navMatch}/`)) return true;
  return false;
}

const ConsoleShell = () => {
  const { t } = useTranslation("app");
  const theme = useTheme();
  const brand = useBrand();
  const location = useLocation();
  const queryClient = useQueryClient();

  // Mesma higiene do portal do representante ao (re)entrar: sem resíduo de tenant.
  // preserveTenantDatabaseKeys só aplica a master com JWT scope=workspace (auth no schema do tenant).
  useEffect(() => {
    clearActiveWorkspaceBrowserStorage({ preserveTenantDatabaseKeysForWorkspaceMaster: true });
    queryClient.clear();
  }, [queryClient]);
  const isMdUp = useMediaQuery(theme.breakpoints.up("md"));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [globalError, setGlobalError] = useState("");
  const [actionToast, setActionToast] = useState("");
  const [refreshing, setRefreshing] = useState(false);
  const { loading: aclLoading, can, financePerson } = usePlatformConsoleAcl();

  const refreshRef = useRef(() => {});

  const stopRefreshing = useCallback(() => setRefreshing(false), []);

  const registerRefresh = useCallback((fn) => {
    refreshRef.current = typeof fn === "function" ? fn : () => {};
  }, []);

  const runRefresh = useCallback(() => {
    setRefreshing(true);
    try {
      refreshRef.current();
    } catch {
      stopRefreshing();
    }
  }, [stopRefreshing]);

  const showActionToast = useCallback((msg) => {
    if (msg) setActionToast(msg);
    stopRefreshing();
  }, [stopRefreshing]);

  const outletContext = useMemo(
    () => ({
      registerRefresh,
      stopRefreshing,
      setGlobalError,
      showActionToast,
    }),
    [registerRefresh, stopRefreshing]
  );

  const onDetail =
    location.pathname.startsWith(`${PLATFORM_CONSOLE_BASE}/workspaces/`) &&
    location.pathname !== PLATFORM_CONSOLE_PATH.workspaces;

  const onWorkspaceCreate = location.pathname === PLATFORM_CONSOLE_PATH.workspaceCreate;
  const isTeamRoute = isConsoleTeamPath(location.pathname);

  const visibleNavItems = useMemo(() => {
    // Enquanto a ACL não carrega, não exibir itens (evita flash de seções negadas).
    if (aclLoading) return [];
    return NAV_ITEMS.filter((item) => {
      if (item.featureEnabled === false) return false;
      if (item.requiresFinancePerson) {
        return Boolean(financePerson?.id);
      }
      const section = item.section || navSectionKeyFromPath(item.path);
      return can(section, "view");
    });
  }, [aclLoading, can, financePerson]);

  const drawer = (
    <Box
      sx={{
        pt: 1,
        pb: 0,
        display: "flex",
        flexDirection: "column",
        flex: 1,
        minHeight: 0,
        height: isMdUp ? "100%" : undefined,
      }}
      component="nav"
      aria-label={t("platformPage.consoleNavAriaLabel")}
    >
      <Typography variant="overline" sx={{ px: 2.5, color: "text.secondary", letterSpacing: 0.6 }}>
        {t("platformPage.consoleBadge")}
      </Typography>
      <List dense sx={{ mt: 0.5, flex: 1 }}>
        {visibleNavItems.map(({ path, match, Icon, labelKey }) => {
          const selected =
            pathMatchesNav(location.pathname, match) || (labelKey === "platformPage.tabWorkspaces" && onDetail);
          return (
            <ListItemButton
              key={path}
              component={RouterLink}
              to={path}
              selected={selected}
              onClick={() => setMobileOpen(false)}
              sx={{
                mx: 1,
                borderRadius: 1,
                "&.Mui-selected": {
                  bgcolor: (th) => alpha(th.palette.primary.main, 0.12),
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                <Icon fontSize="small" color={selected ? "primary" : "inherit"} />
              </ListItemIcon>
              <ListItemText
                primary={t(labelKey, {
                  defaultValue: labelKey === "platformPage.navMyIncentives" ? "Meus resultados" : undefined,
                })}
                primaryTypographyProps={{ fontWeight: selected ? 600 : 500, fontSize: "0.9375rem" }}
              />
            </ListItemButton>
          );
        })}
      </List>
      <Box sx={{ padding: "16px", textAlign: "left", fontSize: "12px", color: theme.palette.text.disabled }}>
        © {new Date().getFullYear()} {brand.name} · v{PLATFORM_APP_VERSION}
      </Box>
    </Box>
  );

  return (
    <Box
      sx={{
        pt: 1,
        pb: isTeamRoute ? 0 : 4,
        display: "flex",
        flexDirection: "column",
        flex: 1,
        minHeight: 0,
        overflow: isTeamRoute ? "hidden" : "visible",
      }}
    >
      <a href="#platform-console-main" style={{ position: "absolute", left: -9999 }}>
        {t("platformPage.skipToContent")}
      </a>
      <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1} sx={{ mb: 1.5 }}>
        <Box sx={{ minWidth: 0, display: "flex", alignItems: "center", gap: 1 }}>
          {!isMdUp ? (
            <IconButton aria-label={t("platformPage.openMenu")} onClick={() => setMobileOpen(true)} edge="start" size="small">
              <MenuIcon />
            </IconButton>
          ) : null}
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h5" component="h1" sx={{ fontWeight: 600, letterSpacing: -0.02, lineHeight: 1.25 }}>
              {t("platformPage.title")}
            </Typography>
            <Typography
              variant="caption"
              component="p"
              color="text.secondary"
              sx={{ display: "block", mt: 0.25, maxWidth: 720, lineHeight: 1.35 }}
            >
              {t("platformPage.consolePurposeOneLiner")}
            </Typography>
          </Box>
        </Box>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center" sx={{ flexShrink: 0 }}>
          {onWorkspaceCreate ? null : (
            <Tooltip title={t("platformPage.refreshSectionTooltip")}>
              <span>
                <BusyButton
                  startIcon={<RefreshIcon />}
                  variant="outlined"
                  size="small"
                  onClick={runRefresh}
                  loading={refreshing}
                  loadingText={t("billingPage.refresh")}
                >
                  {t("billingPage.refresh")}
                </BusyButton>
              </span>
            </Tooltip>
          )}
        </Stack>
      </Stack>

      {globalError ? (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setGlobalError("")}>
          {globalError}
        </Alert>
      ) : null}

      <Snackbar
        open={Boolean(actionToast)}
        autoHideDuration={6000}
        onClose={() => setActionToast("")}
        message={actionToast}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />

      <Box
        sx={{
          display: "flex",
          alignItems: "stretch",
          gap: 0,
          flex: isTeamRoute ? 1 : undefined,
          minHeight: isTeamRoute ? 0 : undefined,
          overflow: isTeamRoute ? "hidden" : "visible",
        }}
      >
        {isMdUp ? (
          <Paper
            variant="outlined"
            sx={{
              width: DRAWER_WIDTH,
              flexShrink: 0,
              borderRadius: 2,
              mr: 2,
              alignSelf: "flex-start",
              position: "sticky",
              top: 16,
              maxHeight: "calc(100vh - 32px)",
              minHeight: "calc(100vh - 32px)",
              overflow: "auto",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {drawer}
          </Paper>
        ) : (
          <Drawer anchor="left" open={mobileOpen} onClose={() => setMobileOpen(false)} PaperProps={{ sx: { width: DRAWER_WIDTH } }}>
            {drawer}
            <Divider />
            <Box sx={{ p: 2 }}>
              <Button fullWidth variant="outlined" onClick={() => setMobileOpen(false)}>
                {t("platformPage.closeMenu")}
              </Button>
            </Box>
          </Drawer>
        )}

        <Box
          id="platform-console-main"
          tabIndex={-1}
          component="main"
          sx={{
            flex: 1,
            minWidth: 0,
            minHeight: isTeamRoute ? 0 : undefined,
            display: isTeamRoute ? "flex" : "block",
            flexDirection: isTeamRoute ? "column" : undefined,
            overflow: isTeamRoute ? "hidden" : "visible",
          }}
        >
          <Outlet context={outletContext} />
        </Box>
      </Box>
    </Box>
  );
};

export default ConsoleShell;
